/*
 * A primeira linha do composer: a ação em negrito
 * com o ícone ("Iniciar Discussão", "Tópico ▾", "Editando postagem") e, quando o
 * tópico de destino não está na tela, o link "🔗 título" para ele.
 *
 * Na resposta, "Tópico ▾" abre o menu de ações (`composer-actions`): responder
 * ao tópico ou responder como tópico vinculado. Minimizado, a linha vira a
 * barra do rascunho: o ícone e o título do tópico (ou o aviso de rascunho).
 */
import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import Dropdown from 'flarum/common/components/Dropdown';
import Button from 'flarum/common/components/Button';
import Link from 'flarum/common/components/Link';
import Icon from 'flarum/common/components/Icon';
import Stream from 'flarum/common/utils/Stream';
import ItemList from 'flarum/common/utils/ItemList';
import { canReplyAsLinkedTopic, replyAsLinkedTopic } from '../features/composer/installLinkedTopic';
import { t } from '../utils/settings';
import { isPhone } from '../motion/motionUtils';

/** Uma ação do menu do cabeçalho: ícone, rótulo em negrito e descrição. */

/** Os ícones das ações (lápis no quadrado, a caixa com a seta, lápis). */
const ICONS = {
  new_topic: 'far fa-pen-to-square',
  reply: 'fas fa-arrow-up-from-bracket',
  edit: 'fas fa-pencil'
};

/** Num composer em tela cheia, seguir um link minimiza antes, como o core. */
function minimizeIfFullScreen(e) {
  if (app.composer.position === 'fullScreen') {
    app.composer.minimize();
    e.stopPropagation();
  }
}

/**
 * Troca o tipo do composer levando o texto já escrito (as ações "Responder
 * ao tópico" / "Iniciar Discussão" do menu). A pergunta "descartar?"
 * não faz sentido: o texto vai junto.
 */
async function switchTo(kind, discussion) {
  const content = app.composer.fields?.content() || '';
  const title = app.composer.fields?.title?.() || '';
  const module = kind === 'reply' ? 'flarum/forum/components/ReplyComposer' : 'flarum/forum/components/DiscussionComposer';
  app.composer.onExit = null;
  await app.composer.load(() => flarum.reg.asyncModuleImport(module), {
    user: app.session.user,
    ...(discussion ? {
      discussion
    } : {}),
    originalContent: content
  });
  if (title && kind === 'new_topic') app.composer.fields.title = Stream(title);
  await app.composer.show();
}
export { switchTo as switchComposer };
export default class ComposerActionTitle extends Component {
  view() {
    if (app.composer.position === 'minimized') return this.draftView();
    return m("div", {
      className: "CanelaComposerAction",
      role: "heading",
      "aria-level": 1
    }, this.trigger(), this.targetLink());
  }

  /** A barra do composer minimizado (`#reply-control.draft .draft-text`). */
  draftView() {
    const {
      kind,
      discussion,
      post,
      title
    } = this.attrs;
    const topic = kind === 'reply' ? discussion : kind === 'edit' ? post?.discussion() : null;
    const text = topic ? topic.title() : title?.() || t('composer.shell.saved_draft');
    return m("div", {
      className: "CanelaComposerAction CanelaComposerAction--draft"
    }, topic ? m(Icon, {
      name: ICONS.reply
    }) : null, m("span", {
      className: "CanelaComposerAction-draftText"
    }, text));
  }
  label() {
    const labels = {
      new_topic: t('composer.shell.action_new_topic'),
      reply: t('composer.shell.action_reply'),
      edit: t('composer.shell.action_edit')
    };
    return this.attrs.label || labels[this.attrs.kind] || '';
  }
  icon() {
    return this.attrs.icon || ICONS[this.attrs.kind] || ICONS.new_topic;
  }
  trigger() {
    const label = this.label();
    const actions = this.actionItems().toArray();

    // Sem outras ações, o rótulo fica parado, sem a seta.
    if (!actions.length) {
      return m("span", {
        className: "CanelaComposerAction-trigger CanelaComposerAction-trigger--static"
      }, m(Icon, {
        name: this.icon()
      }), m("span", {
        className: "CanelaComposerAction-label"
      }, label));
    }
    return m(Dropdown, {
      className: "CanelaComposerAction-menu",
      buttonClassName: "Button CanelaComposerAction-trigger",
      menuClassName: "CanelaComposerAction-dropdown",
      icon: this.icon(),
      caretIcon: "fas fa-angle-down",
      label: m("span", {
        className: "CanelaComposerAction-label"
      }, label),
      accessibleToggleLabel: t('composer.linked_topic.menu_label')
    }, actions.map(action => this.actionButton(action.icon, action.label, action.description, action.onclick)));
  }

  /**
   * As OUTRAS ações do menu (`composer-actions`): a atual não aparece. Trocar
   * de tipo leva o título e o texto. É o ponto de extensão do menu: outro
   * recurso (as mensagens privadas) estende este método para pôr "Criar
   * mensagem" / "Responder como mensagem privada":
   *
   *   extend(ComposerActionTitle.prototype, 'actionItems', function (items) {
   *     items.add('message', { icon, label, description, onclick }, 50);
   *   });
   *
   * `this.attrs.kind` diz qual composer está aberto ('new_topic' | 'reply' |
   * 'edit', ou o que o outro composer passar).
   */
  actionItems() {
    const {
      kind,
      discussion
    } = this.attrs;
    const items = new ItemList();
    const viewing = app.current.get('discussion') || null;
    const canStart = !!app.forum.attribute('canStartDiscussion');
    if (kind === 'new_topic' && viewing && viewing.canReply?.()) {
      items.add('reply', {
        icon: ICONS.reply,
        label: t('composer.linked_topic.reply'),
        description: t('composer.shell.reply_to_topic_desc'),
        onclick: () => switchTo('reply', viewing)
      }, 100);
    }
    if (kind === 'reply' && discussion) {
      if (canReplyAsLinkedTopic()) {
        items.add('linkedTopic', {
          icon: 'fas fa-plus',
          label: t('composer.linked_topic.new_topic'),
          description: t('composer.shell.reply_as_new_topic_desc'),
          onclick: () => replyAsLinkedTopic(discussion)
        }, 100);
      }
      if (canStart) {
        items.add('newTopic', {
          icon: ICONS.new_topic,
          label: t('composer.shell.action_create_topic'),
          description: t('composer.shell.create_topic_desc'),
          onclick: () => switchTo('new_topic', null)
        }, 90);
      }
    }

    // Outro composer (o de mensagens) que reutilize este cabeçalho pode
    // voltar para tópico.
    if (kind !== 'new_topic' && kind !== 'reply' && kind !== 'edit' && canStart) {
      items.add('newTopic', {
        icon: ICONS.new_topic,
        label: t('composer.shell.action_create_topic'),
        description: t('composer.shell.create_topic_desc'),
        onclick: () => switchTo('new_topic', null)
      }, 90);
    }
    return items;
  }

  /** Um item do menu de ações: ícone, rótulo em negrito e a descrição. */
  actionButton(icon, label, description, onclick, current = false) {
    // A ação atual não entra mais no menu; `current` fica
    // para quem estender.
    return m(Button, {
      className: `Button CanelaComposerAction-btn${current ? ' CanelaComposerAction-btn--current' : ''}`,
      onclick: onclick || undefined,
      disabled: current
    }, m("span", {
      className: "CanelaComposerAction-btnIcon"
    }, m(Icon, {
      name: icon
    })), m("span", {
      className: "CanelaComposerAction-btnTexts"
    }, m("span", {
      className: "CanelaComposerAction-btnLabel"
    }, label), m("span", {
      className: "CanelaComposerAction-btnDescription"
    }, description)));
  }

  /**
   * "🔗 título": o destino da resposta ou da edição. Na resposta, só quando o
   * tópico não está aberto atrás do composer — ele some ao responder do
   * próprio tópico. No celular, sempre: lá o editor
   * ocupa a tela e esconde o tópico.
   */
  targetLink() {
    const {
      kind,
      discussion,
      post
    } = this.attrs;

    // No celular o editor cobre o tópico inteiro: o título aparece sempre.
    if (kind === 'reply' && discussion && (!app.viewingDiscussion(discussion) || isPhone())) {
      return m(Link, {
        className: "CanelaComposerAction-target",
        href: app.route.discussion(discussion),
        onclick: minimizeIfFullScreen,
        title: discussion.title()
      }, m(Icon, {
        name: "fas fa-link"
      }), m("span", {
        className: "CanelaComposerAction-targetLabel"
      }, discussion.title()));
    }
    if (kind === 'edit' && post) {
      const target = post.discussion();
      const text = app.translator.trans('core.forum.composer_edit.post_link', {
        number: post.number(),
        discussion: target.title()
      }, true);
      return m(Link, {
        className: "CanelaComposerAction-target",
        href: app.route.discussion(target, post.number()),
        onclick: minimizeIfFullScreen,
        title: text
      }, m(Icon, {
        name: "fas fa-link"
      }), m("span", {
        className: "CanelaComposerAction-targetLabel"
      }, text));
    }
    return null;
  }
}
flarum.reg.add('ramon-canela', 'forum/composer/ComposerActionTitle', ComposerActionTitle);